import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import './MegaMenu.css';

// ── Panel definitions ──

const COMMERCE_ITEMS = [
  {
    title: 'COMMODITY PRICES',
    desc: 'Live prices across every terminal',
    route: '/market-overview',
    statLabel: 'commodities',
    statKey: 'commodities',
  },
  {
    title: 'ROUTE PLANNER',
    desc: 'Optimize runs by ship and cargo capacity',
    route: '/route-planner',
    statLabel: null,
    staticStat: 'Integrated route builder',
  },
  {
    title: 'EXCHANGE',
    desc: 'Buy, sell, and trade with other players',
    route: '/exchange',
    statLabel: null,
    staticStat: 'Chat \u00b7 Handle handshake \u00b7 Dual confirmation',
  },
  {
    title: 'LOCATIONS & MERCHANTS',
    desc: "What's available at every station",
    route: '/locations',
    statLabel: null,
    staticStat: '100+ mapped locations',
  },
];

const GEAR_ITEMS = [
  {
    title: 'ITEMS DATABASE',
    desc: 'Weapons, armor, components, and equipment with prices',
    route: '/items',
    statKey: 'items',
    statLabel: 'items tracked',
  },
  {
    title: 'MOST WANTED',
    desc: 'Community bounty board. Request missing items, vote, and hunt them down.',
    route: '/most-wanted',
    statLabel: null,
    staticStat: 'Vote \u00b7 Request \u00b7 Hunt',
  },
  {
    title: 'SHOPS & LOCATIONS',
    desc: 'Where to buy and loot items across the verse',
    route: '/database/locations',
    statLabel: null,
    staticStat: 'Merchants + loot drops',
  },
  {
    title: 'GEAR LOADOUTS',
    desc: 'Build armor and weapon loadouts, find where to buy or loot each piece',
    route: '/gear-loadouts',
    statLabel: null,
  },
  {
    title: 'SUBMIT A CORRECTION',
    desc: 'Is something wrong? Help improve the data.',
    route: '/submit-data',
    statLabel: null,
  },
];

const SHIPS_ITEMS = [
  {
    title: 'SHIP MATRIX',
    desc: 'Full specs, comparison, and variant details',
    route: '/ship-matrix',
    statKey: 'ships',
    statLabel: 'ships',
  },
  {
    title: 'SHIP PLANNER',
    desc: 'Browse components, plan loadouts, and build a shopping list — free',
    route: '/ship-planner',
    statKey: 'slots',
    statLabel: 'component slots',
  },
  {
    title: 'WIKELO SHIPS',
    desc: 'Wikelo Program rewards and requirements',
    route: '/info/wikelo',
    statLabel: null,
  },
  {
    title: 'EXECUTIVE HANGAR',
    desc: 'Executive Hangar exclusive ships',
    route: '/info/executive-hangar',
    statLabel: null,
  },
];

const INDUSTRY_ITEMS = [
  {
    title: 'MINING',
    desc: 'Minerals, ores & equipment',
    route: '/industry/mining',
    statLabel: null,
  },
  {
    title: 'REFINING',
    desc: 'Refinery station comparison',
    route: '/industry/refining',
    statLabel: null,
  },
  {
    title: 'ROUTE PLANNER',
    desc: 'Mine → Refine → Sell optimizer',
    route: '/industry/mining/route',
    statLabel: null,
  },
  {
    title: 'CRAFTING',
    desc: 'Blueprints, recipes & ingredients',
    route: '/industry/crafting',
    statLabel: null,
  },
];

const CREW_ITEMS = [
  {
    title: 'CREWLINK',
    desc: 'Find squadmates for any mission type',
    route: '/crewlink',
    statLabel: null,
    staticStat: 'Combat \u00b7 Mining \u00b7 Salvage \u00b7 Exploration \u00b7 Medical SOS',
  },
  {
    title: 'LEADERBOARDS',
    desc: 'Top contributors and community rankings',
    route: '/leaderboards',
    statLabel: null,
  },
  {
    title: 'ORGANIZATIONS',
    desc: 'Fleet, cargo, DKP, and 20+ management tools for your org',
    route: '/features/orgs',
    statLabel: null,
    staticStat: 'Free to create \u00b7 Unlimited members',
  },
];

const PANELS = [
  { key: 'commerce', label: 'Commerce', items: COMMERCE_ITEMS },
  { key: 'gear', label: 'Gear', items: GEAR_ITEMS },
  { key: 'ships', label: 'Ships', items: SHIPS_ITEMS, gridClass: 'mega-menu__grid--3col' },
  { key: 'industry', label: 'Industry', items: INDUSTRY_ITEMS, gridClass: 'mega-menu__grid--3col' },
  { key: 'crew', label: 'Crew', items: CREW_ITEMS },
];

// ── Live data hook (with module-level cache to avoid duplicate fetches) ──

let _countsCache = null;
let _countsCacheTimestamp = 0;
const COUNTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function useLiveCounts() {
  const [counts, setCounts] = useState(_countsCache || {});

  useEffect(() => {
    // Return cached data if still fresh
    if (_countsCache && Date.now() - _countsCacheTimestamp < COUNTS_CACHE_TTL) {
      setCounts(_countsCache);
      return;
    }

    let cancelled = false;

    const fetchCounts = async () => {
      try {
        const [itemsRes, shipsRes, slotsRes, commoditiesRes] = await Promise.all([
          supabase.from('items').select('*', { count: 'exact', head: true }),
          supabase.from('ships').select('*', { count: 'exact', head: true }),
          supabase.from('ship_component_slots').select('*', { count: 'exact', head: true }),
          supabase.from('items').select('*', { count: 'exact', head: true }).eq('category', 'Commodities'),
        ]);

        if (!cancelled) {
          const newCounts = {
            items: itemsRes.count || null,
            ships: shipsRes.count || null,
            slots: slotsRes.count || null,
            commodities: commoditiesRes.count || null,
          };
          _countsCache = newCounts;
          _countsCacheTimestamp = Date.now();
          setCounts(newCounts);
        }
      } catch (err) {
        console.error('[MegaMenu] Error fetching live counts:', err);
      }
    };

    fetchCounts();
    return () => { cancelled = true; };
  }, []);

  return counts;
}

// Format count for display
function formatCount(count) {
  if (count == null) return null;
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k+`;
  }
  return `${count}+`;
}

// ── Panel Item Component ──

const PanelItem = ({ item, counts, onClick }) => {
  const statNumber = item.statKey ? formatCount(counts[item.statKey]) : null;

  if (item.comingSoon) {
    return (
      <div className="mega-menu__item mega-menu__item--coming-soon" role="menuitem" aria-disabled="true">
        <div className="mega-menu__item-title">{item.title}</div>
        <div className="mega-menu__item-desc">{item.desc}</div>
        {item.staticStat && (
          <div className="mega-menu__item-stat">
            <span className="mega-menu__item-stat-number">{item.staticStat}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.route}
      className="mega-menu__item"
      role="menuitem"
      onClick={onClick}
    >
      <div className="mega-menu__item-title">{item.title}</div>
      <div className="mega-menu__item-desc">{item.desc}</div>
      {(statNumber || item.staticStat) && (
        <div className="mega-menu__item-stat">
          <span className="mega-menu__item-stat-number">{statNumber || item.staticStat}</span>
          {item.statLabel && <span className="mega-menu__item-stat-label">{item.statLabel}</span>}
        </div>
      )}
    </Link>
  );
};

// ── Desktop Mega Menu ──

const MegaMenu = ({ onNavigate }) => {
  const [activePanel, setActivePanel] = useState(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const counts = useLiveCounts();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const handleLabelEnter = useCallback((panelKey) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    showTimeoutRef.current = setTimeout(() => {
      setActivePanel(panelKey);
    }, 150);
  }, []);

  const handleLabelLeave = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setActivePanel(null);
    }, 300);
  }, []);

  const handlePanelEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handlePanelLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setActivePanel(null);
    }, 300);
  }, []);

  const handleItemClick = useCallback(() => {
    setActivePanel(null);
    if (onNavigate) onNavigate();
  }, [onNavigate]);

  // Keyboard handling
  const handleKeyDown = useCallback((e, panelKey) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActivePanel(prev => prev === panelKey ? null : panelKey);
    } else if (e.key === 'Escape') {
      setActivePanel(null);
    }
  }, []);

  return (
    <ul className="mega-menu" role="menubar">
      {PANELS.map((panel) => (
        <li
          key={panel.key}
          className="mega-menu__trigger"
          onMouseEnter={() => handleLabelEnter(panel.key)}
          onMouseLeave={handleLabelLeave}
        >
          <button
            className="mega-menu__label"
            aria-haspopup="true"
            aria-expanded={activePanel === panel.key}
            onKeyDown={(e) => handleKeyDown(e, panel.key)}
          >
            {panel.label}
          </button>

          {activePanel === panel.key && (
            <div
              className="mega-menu__panel mega-menu__panel--visible"
              role="menu"
              aria-label={`${panel.label} menu`}
              onMouseEnter={handlePanelEnter}
              onMouseLeave={handlePanelLeave}
            >
              <div className={`mega-menu__grid ${panel.gridClass || ''}`}>
                {panel.items.map((item) => (
                  <PanelItem
                    key={item.title}
                    item={item}
                    counts={counts}
                    onClick={handleItemClick}
                  />
                ))}
              </div>

              {panel.hasComingSoonFooter && (
                <div className="mega-menu__coming-soon-footer">
                  <span className="mega-menu__coming-soon-label">Coming Soon</span>
                  <span className="mega-menu__coming-soon-items">
                    Mining &middot; Refining &middot; Crafting
                  </span>
                </div>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

// ── Mobile Accordion Menu ──

const MegaMenuMobile = ({ onNavigate }) => {
  const [openSection, setOpenSection] = useState(null);
  const counts = useLiveCounts();

  const toggleSection = (key) => {
    setOpenSection(prev => prev === key ? null : key);
  };

  const handleItemClick = () => {
    setOpenSection(null);
    if (onNavigate) onNavigate();
  };

  return (
    <ul className="mega-menu-mobile">
      {PANELS.map((panel) => (
        <li key={panel.key} className="mega-menu-mobile__section">
          <button
            className="mega-menu-mobile__trigger"
            onClick={() => toggleSection(panel.key)}
            aria-expanded={openSection === panel.key}
            aria-haspopup="true"
          >
            {panel.label}
            <span className={`mega-menu-mobile__arrow ${openSection === panel.key ? 'mega-menu-mobile__arrow--open' : ''}`}>
              &#9660;
            </span>
          </button>

          <div className={`mega-menu-mobile__content ${openSection === panel.key ? 'mega-menu-mobile__content--open' : ''}`}>
            {panel.items.map((item) => {
              if (item.comingSoon) {
                return (
                  <div key={item.title} className="mega-menu-mobile__link mega-menu-mobile__link--coming-soon">
                    <div className="mega-menu-mobile__link-title">{item.title}</div>
                    <div className="mega-menu-mobile__link-desc">{item.staticStat || item.desc}</div>
                  </div>
                );
              }

              const statNumber = item.statKey ? formatCount(counts[item.statKey]) : null;

              return (
                <Link
                  key={item.title}
                  to={item.route}
                  className="mega-menu-mobile__link"
                  onClick={handleItemClick}
                >
                  <div className="mega-menu-mobile__link-title">
                    {item.title}
                    {statNumber && (
                      <span className="mega-menu-mobile__stat-number">
                        {statNumber}
                      </span>
                    )}
                  </div>
                  <div className="mega-menu-mobile__link-desc">{item.desc}</div>
                </Link>
              );
            })}

            {panel.hasComingSoonFooter && (
              <div className="mega-menu-mobile__coming-soon-footer">
                Coming Soon: Mining &middot; Refining &middot; Crafting
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export { MegaMenu, MegaMenuMobile, useLiveCounts, formatCount };
export default MegaMenu;
